import { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Switch,
  Button,
  message,
  Input,
  Space
} from "antd";
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { countryService, Country } from "../services/countryService";
import TableFilter, { FilterConfig } from "../components/TableFilter/TableFilter";
import { useOptimizedPageSearch } from "../hooks/useOptimizedPageSearch";
import styles from "./Countries.module.css";
import ReactCountryFlag from 'react-country-flag';
import getCountryISO2 from 'country-iso-3-to-2';
import { getCountryOptions } from "../constants/countries";

function Countries() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);

  const enrichCountries = (backendCountries: Country[]) => {
    const worldCountries = getCountryOptions();

    return backendCountries.map(bc => {
      let iso2 = bc.code;
      try {
        iso2 = getCountryISO2(bc.code); 
      } catch {
        iso2 = bc.code; 
      }

      const match = worldCountries.find(
        wc => wc.value.toLowerCase() === iso2.toLowerCase()
      );

      return {
        ...bc,
        phonePrefix: match?.phonePrefix || '',
        currency: match?.currency || ''
      };
    });
  };

  const fetchCountries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await countryService.getAll();
      const enriched = enrichCountries(response.data);
      setCountries(enriched);
    } catch (error) {
      message.error(t('countries.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Utiliser le hook optimisé pour la recherche et les filtres
  const {
    filteredData: filteredCountries,
    handleFilterChange,
    handleClearFilters
  } = useOptimizedPageSearch({
    data: countries,
    searchFields: ['name', 'code', 'continent', 'isActive']
  });

  // Configuration des filtres
  const filterConfig: FilterConfig[] = [
    {
      key: 'country',
      type: 'text',
      label: t('countries.countryName'),
      placeholder: t('filters.searchByName')
    },
    {
      key: 'code',
      type: 'text',
      label: t('countries.countryCode'),
      placeholder: t('filters.searchByCode')
    },
    {
      key: 'currency',
      type: 'text',
      label: t('countries.currency'),
      placeholder: t('filters.searchByCurrency')
    },
    {
      key: 'selected',
      type: 'select',
      label: t('common.status'),
      options: [
        { label: t('common.active'), value: true },
        { label: t('common.inactive'), value: false }
      ]
    }
  ];

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const handleStatusChange = async (id: number) => {
    try {
      await countryService.update(id);
      message.success(t('countries.statusUpdateSuccess'));
      fetchCountries();
    } catch (error) {
      message.error(t('countries.statusUpdateError'));
    }
  };

  const columns = [
    {
      title: t('countries.country'),
      dataIndex: "country",
      key: "country",
      sorter: (a, b) => a.name.localeCompare(b.name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className={styles.filterContainer}>
          <Input
            placeholder={t('filters.searchByName')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
      render: (name: string, record: Country) => (
        <div className={styles.countryImageContainer}>
          <ReactCountryFlag
            countryCode={getCountryISO2(record.code)}
            svg
            style={{
              width: '2em',
              height: '2em',
            }}
            title={name}
          />
          {name}
        </div>
      ),
    },
    {
      title: t('countries.code'),
      dataIndex: "code",
      key: "code",
      sorter: (a, b) => a.code.localeCompare(b.code),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className={styles.filterContainer}>
          <Input
            placeholder={t('filters.searchByCode')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.code.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('countries.phonePrefix'),
      dataIndex: "phonePrefix",
      key: "phonePrefix",
      sorter: (a, b) => a.phonePrefix.localeCompare(b.phonePrefix),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className={styles.filterContainer}>
          <Input
            placeholder={t('filters.searchByPrefix')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.phonePrefix.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('countries.currency'),
      dataIndex: "currency",
      key: "currency",
      sorter: (a, b) => a.currency.localeCompare(b.currency),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className={styles.filterContainer}>
          <Input
            placeholder={t('filters.searchByCurrency')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.currency.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.status'),
      key: "selected",
      dataIndex: "selected",
      render: (selected: boolean, record: Country) => (
        <Switch
          checked={selected}
          onChange={(checked) => handleStatusChange(record.id)}
        />
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={t('countries.title')}
          >
            <TableFilter
              filters={filterConfig}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={filteredCountries}
                loading={loading}
                className="ant-border-space"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Countries;
